import React, { useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Loader2, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUPS = [
  { key: 'clash', title: 'Clash', tone: 'border-red-200 bg-red-50 text-red-800' },
  { key: 'busy', title: 'Busy that day', tone: 'border-amber-200 bg-amber-50 text-amber-800' },
  { key: 'free', title: 'Free', tone: 'border-green-200 bg-green-50 text-green-800' },
];

// Admin: who is free, busy or clashing on a job's date. Collapsed by default.
export const JobAvailability = ({ job, token }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || data) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/jobs/${job.id}/availability`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load availability');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 rounded-[10px] border border-hair bg-page"
         data-testid={`availability-${job.id}`}>
      <button type="button" onClick={toggle}
              className="flex w-full items-center justify-between gap-2 p-3 text-left"
              data-testid={`availability-toggle-${job.id}`}>
        <span className="text-xs font-semibold text-slate-600">
          Availability{data ? `: ${data.summary}` : ''}
        </span>
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          : open ? <ChevronDown className="h-4 w-4 text-slate-500" />
            : <ChevronRight className="h-4 w-4 text-slate-500" />}
      </button>

      {open && data && (
        <div className="space-y-3 border-t border-hair p-3" data-testid={`availability-list-${job.id}`}>
          {!data.job_time_known && (
            <p className="text-[11px] text-slate-500">
              This job's time could not be read, so busy times that day show as amber.
            </p>
          )}
          {GROUPS.map(({ key, title, tone }) => (
            data[key].length > 0 && (
              <div key={key}>
                <p className="mb-1 text-[11px] font-semibold text-slate-500">
                  {title} ({data[key].length})
                </p>
                <div className="space-y-1.5">
                  {data[key].map((person) => (
                    <div key={person.email}
                         className={`rounded-[8px] border px-2.5 py-1.5 text-xs ${tone}`}
                         data-testid={`availability-${key}-${person.email}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold">{person.name}</span>
                        {person.maps_url && (
                          <a href={person.maps_url} target="_blank" rel="noreferrer"
                             className="flex items-center gap-1 underline"
                             data-testid={`availability-travel-${person.email}`}>
                            <Navigation className="h-3 w-3" /> Travel time
                          </a>
                        )}
                      </div>
                      {key !== 'free' && (
                        <p className="mt-0.5">
                          Busy {person.busy_label}
                          {person.location ? ` · ${person.location}` : ''}
                          {person.miles !== null && person.miles !== undefined
                            ? ` · about ${person.miles} miles apart` : ''}
                        </p>
                      )}
                      {person.note && <p className="mt-0.5 opacity-80">{person.note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default JobAvailability;
